/**
 * SFD v2 — Slickflow Designer built on epx-flow
 *
 * Entry point: imports EpxFlow and registers all SFD plugins
 * via the extension APIs (registerProvider, addModdleExtension,
 * addReaderExtension, addWriterExtension, addDefaultProps, addTranslations).
 */
import EpxFlow from '@epx/flow';
import { EpxDmn, VIEWS as DMN_VIEWS } from '@epx/dmn';

// SFD Providers
import { SfdActionPropertiesProvider } from './plugins/providers/SfdActionPropertiesProvider';
import { SfdPerformerPropertiesProvider } from './plugins/providers/SfdPerformerPropertiesProvider';
import { SfdAdminProcessPropertiesProvider } from './plugins/providers/SfdAdminProcessPropertiesProvider';
import { SfdMultiSignPropertiesProvider } from './plugins/providers/SfdMultiSignPropertiesProvider';
import { SfdNotificationPropertiesProvider } from './plugins/providers/SfdNotificationPropertiesProvider';
import { SfdDocumentManagementPropertiesProvider } from './plugins/providers/SfdDocumentManagementPropertiesProvider';
import { SfdSectionPropertiesProvider } from './plugins/providers/SfdSectionPropertiesProvider';

// SFD Extensions for mixed BPMN providers
import { SfdGatewayExtension } from './plugins/providers/SfdGatewayExtension';
import { SfdTransitionExtension } from './plugins/providers/SfdTransitionExtension';
import { SfdBoundaryExtension } from './plugins/providers/SfdBoundaryExtension';
import { SfdServiceTaskExtension } from './plugins/providers/SfdServiceTaskExtension';
import { SfdScriptTaskExtension } from './plugins/providers/SfdScriptTaskExtension';
import { SfdSubProcessExtension } from './plugins/providers/SfdSubProcessExtension';
import { SfdTriggerExtension } from './plugins/providers/SfdTriggerExtension';
import { SfdExpressionExtension } from './plugins/providers/SfdExpressionExtension';
import { SfdGroupFilterProvider } from './plugins/providers/SfdGroupFilterProvider';
import { SfdBusinessRuleExtension } from './plugins/providers/SfdBusinessRuleExtension';

// SFD Serialization
import sfDescriptor from './plugins/serialization/sf.json';
import { SfdReaderPlugin } from './plugins/serialization/SfdReaderPlugin';
import { SfdWriterPlugin } from './plugins/serialization/SfdWriterPlugin';

// SFD Default Props
import { SFD_DEFAULT_PROPS, resolveSfdDefaults } from './plugins/interceptors/SfdDefaultProps';

// SFD i18n
import sfdEn from './i18n/en.json';
import sfdEs from './i18n/es.json';
import sfdPt from './i18n/pt.json';

// ── SFD Custom Themes ────────────────────────────────────────────────────────
import { SFD_THEMES } from './themes/sfdThemes';

// ── AI Assistant ─────────────────────────────────────────────────────────────
import { AiAssistant } from './ai/AiAssistant';

// ── DMN overlay presentation modes ───────────────────────────────────────────
import {
  mountDmnOverlayModes,
  showOverlay as showDmnOverlay,
  hideOverlay as hideDmnOverlay,
} from './plugins/dmnOverlayModes';

// ── Theme & Locale metadata (label + icon for dynamic menus) ─────────────────
const THEME_META = {
  default:      { label: 'Default',       icon: '🎨' },
  dark:         { label: 'Dark',          icon: '🌑' },
  highContrast: { label: 'High Contrast', icon: '👀' },
  nord:         { label: 'Nord',          icon: '❄️' },
  ocean:        { label: 'Ocean',         icon: '🌊' },
  dracula:      { label: 'Dracula',       icon: '🦇' },
  sunset:       { label: 'Sunset',        icon: '🌇' },
  pastel:       { label: 'Pastel',        icon: '🌸' },
};

const LOCALE_META = {
  en: 'English',
  es: 'Español',
  pt: 'Português',
};

const GRID_OPTIONS = [
  { key: 'off',         label: 'Off',           type: null,   size: 0  },
  { key: 'mesh-fine',   label: 'Lines · Fine',  type: 'mesh', size: 10 },
  { key: 'mesh-normal', label: 'Lines · Normal',type: 'mesh', size: 20 },
  { key: 'mesh-large',  label: 'Lines · Large', type: 'mesh', size: 40 },
  { key: 'dot-fine',    label: 'Dots · Fine',   type: 'dot',  size: 10 },
  { key: 'dot-normal',  label: 'Dots · Normal', type: 'dot',  size: 20 },
  { key: 'dot-large',   label: 'Dots · Large',  type: 'dot',  size: 40 },
];

let _currentGridKey = 'off';


// ── Starter diagram ──────────────────────────────────────────────────────────

const STARTER_XML = `<?xml version="1.0" encoding="UTF-8"?>
<definitions xmlns="http://www.omg.org/spec/BPMN/20100524/MODEL"
             xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
             xmlns:sf="http://www.slickflow.com/schema/sf"
             xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI"
             xmlns:dc="http://www.omg.org/spec/DD/20100524/DC"
             targetNamespace="http://www.epx.com/schema/1.0"
             id="Definitions_1">
  <process id="Process_1" isExecutable="true">
    <startEvent id="start1" name="Start" sf:guid="a1b2c3d4-0001-0001-0001-000000000001"/>
  </process>
  <bpmndi:BPMNDiagram id="BPMNDiagram_1">
    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="Process_1">
      <bpmndi:BPMNShape id="start1_di" bpmnElement="start1">
        <dc:Bounds x="152" y="172" width="36" height="36"/>
      </bpmndi:BPMNShape>
    </bpmndi:BPMNPlane>
  </bpmndi:BPMNDiagram>
</definitions>`;

// ── State ─────────────────────────────────────────────────────────────────────

let epxFlow = null;
let modalMode = 'import';

// ── DMN State ─────────────────────────────────────────────────────────────────

let epxDmn = null;
let activeDmnNodeId = null;
// Keyed by nodeId (stable) so renaming decisionRef or reusing it across two
// BusinessRuleTasks doesn't clobber or steal in-session edits.
const dmnStore = new Map(); // nodeId → DMN XML string

function simStorageKey(nodeId) {
  return `sfd-v2.dmn-scenarios.${nodeId || '_default'}`;
}

// ── SFD Plugin Registration ──────────────────────────────────────────────────

function registerSfdPlugins(instance) {
  // Moddle extension (must be before importXML)
  instance.addModdleExtension('sf', sfDescriptor);

  // Reader/writer plugins
  instance.addReaderExtension(new SfdReaderPlugin());
  instance.addWriterExtension(new SfdWriterPlugin());

  // Default props
  for (const [elementType, defaults] of Object.entries(SFD_DEFAULT_PROPS)) {
    instance.addDefaultProps(elementType, resolveSfdDefaults(defaults));
  }

  // Translations
  instance.addTranslations('en', sfdEn);
  instance.addTranslations('es', sfdEs);
  instance.addTranslations('pt', sfdPt);

  // Property providers
  const sfdProviders = [
    SfdActionPropertiesProvider,
    SfdPerformerPropertiesProvider,
    SfdAdminProcessPropertiesProvider,
    SfdMultiSignPropertiesProvider,
    SfdNotificationPropertiesProvider,
    SfdDocumentManagementPropertiesProvider,
    SfdSectionPropertiesProvider,
    SfdGatewayExtension,
    SfdTransitionExtension,
    SfdBoundaryExtension,
    SfdServiceTaskExtension,
    SfdScriptTaskExtension,
    SfdSubProcessExtension,
    SfdTriggerExtension,
    SfdExpressionExtension,
    SfdGroupFilterProvider,
  ];

  for (const ProviderClass of sfdProviders) {
    instance.registerProvider(new ProviderClass(instance._lf, instance._i18n), 500);
  }

  // BusinessRule extension (non-class instantiation pattern — already instantiated)
  instance.registerProvider(new SfdBusinessRuleExtension(instance._lf, instance._i18n), 500);
}

// ── Dynamic menu population ───────────────────────────────────────────────────

function populateMenus() {
  // Theme menu
  const themeMenu = document.getElementById('theme-menu');
  if (themeMenu && epxFlow) {
    themeMenu.innerHTML = '';
    const builtIn = ['default', 'dark', 'highContrast'];
    const all = epxFlow.getRegisteredThemes();
    const custom = all.filter(t => !builtIn.includes(t));

    builtIn.forEach(name => {
      const meta = THEME_META[name] || { label: name, icon: '' };
      const btn = document.createElement('button');
      btn.className = 'dropdown-item';
      btn.textContent = `${meta.icon} ${meta.label}`.trim();
      btn.onclick = () => changeTheme(name);
      themeMenu.appendChild(btn);
    });

    if (custom.length) {
      const div = document.createElement('div');
      div.className = 'dropdown-divider';
      themeMenu.appendChild(div);

      custom.forEach(name => {
        const meta = THEME_META[name] || { label: name.charAt(0).toUpperCase() + name.slice(1), icon: '' };
        const btn = document.createElement('button');
        btn.className = 'dropdown-item';
        btn.textContent = `${meta.icon} ${meta.label}`.trim();
        btn.onclick = () => changeTheme(name);
        themeMenu.appendChild(btn);
      });
    }
  }

  // Locale menu
  const localeMenu = document.getElementById('locale-menu');
  if (localeMenu && epxFlow) {
    localeMenu.innerHTML = '';
    epxFlow.getRegisteredLocales().forEach(code => {
      const label = LOCALE_META[code] || code.toUpperCase();
      const btn = document.createElement('button');
      btn.className = 'dropdown-item';
      btn.textContent = label;
      btn.onclick = () => changeLocale(code);
      localeMenu.appendChild(btn);
    });
  }

  // Grid menu
  const gridMenu = document.getElementById('grid-menu');
  if (gridMenu) {
    gridMenu.innerHTML = '';
    let lastType = undefined;
    GRID_OPTIONS.forEach(opt => {
      // Insert divider when switching between groups (null→mesh, mesh→dot)
      if (lastType !== undefined && opt.type !== lastType) {
        const div = document.createElement('div');
        div.className = 'dropdown-divider';
        gridMenu.appendChild(div);
      }
      lastType = opt.type;
      const btn = document.createElement('button');
      btn.className = 'dropdown-item' + (opt.key === _currentGridKey ? ' dropdown-item--active' : '');
      btn.dataset.gridKey = opt.key;
      btn.textContent = opt.label;
      btn.onclick = () => changeGrid(opt.key);
      gridMenu.appendChild(btn);
    });
  }
}

// ── Statusbar helpers ─────────────────────────────────────────────────────────

function updateZoomStatus() {
  try {
    const scale = epxFlow?._lf?.graphModel?.transformModel?.SCALE_X;
    if (scale != null) {
      const el = document.getElementById('sb-zoom');
      if (el) el.textContent = Math.round(scale * 100) + '%';
    }
  } catch (_) {}
}

function setStatusbarFile(label) {
  const el = document.getElementById('sb-file-label');
  if (el) el.textContent = label || 'New diagram';
}

function setStatusbarStatus(state, label) {
  const dot = document.getElementById('sb-status-dot');
  const lbl = document.getElementById('sb-status-label');
  if (dot) {
    dot.className = 'statusbar-dot';
    if (state === 'ok')   dot.classList.add('statusbar-dot--ok');
    if (state === 'warn') dot.classList.add('statusbar-dot--warn');
    if (state === 'err')  dot.classList.add('statusbar-dot--err');
  }
  if (lbl) lbl.textContent = label || 'Ready';
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function resetDefaultView(instance) {
  const tm = instance._lf.graphModel.transformModel;
  tm.SCALE_X = 0.75;
  tm.SCALE_Y = 0.75;
  instance._lf.translateCenter();
  updateZoomStatus();
}

// ── Init ─────────────────────────────────────────────────────────────────────

function initModeler(locale = 'en') {
  if (epxFlow) {
    epxFlow.destroy();
  }

  epxFlow = new EpxFlow({
    container: '#canvas',
    propertiesContainer: '#properties',
    paletteContainer: '#palette',
    locale,
    grid: false,
  });

  // Register SFD custom themes
  for (const [name, theme] of Object.entries(SFD_THEMES)) {
    epxFlow.registerTheme(name, theme);
  }

  // Register all SFD plugins
  registerSfdPlugins(epxFlow);

  // Events
  epxFlow.on('element.changed', ({ element }) => {
    console.log('Modified:', element.id);
  });

  // Show properties panel only on single node/edge click, not on lasso or blank
  const propEl = document.getElementById('properties');
  if (propEl) propEl.classList.add('is-hidden');
  const lf = epxFlow.getLogicFlow();
  lf.on('propertiesPanel.show', () => propEl?.classList.remove('is-hidden'));
  lf.on('propertiesPanel.hide', () => propEl?.classList.add('is-hidden'));

  window.epxFlow = epxFlow;

  // Re-attach AI selection listener whenever a new flow instance is created
  window.aiAssistant?.attachToFlow(epxFlow);

  // ── Init DMN editor (once) ────────────────────────────────────────────────
  if (!epxDmn) {
    epxDmn = new EpxDmn({
      container: '#dmn-editor',
      propertiesContainer: '#dmn-properties',
      view: DMN_VIEWS.DRD,
      // Match sfd-v2 defaults so BPMN and DMN feel like one product.
      locale,
      theme: 'default',
      grid: false,
      simulator: { storageKey: simStorageKey(null) },
    });
    window.epxDmn = epxDmn;

    // Mirror sfd-v2 i18n into the DMN editor and register the same custom
    // themes so the host-level theme switcher controls both modelers.
    epxDmn.addTranslations('en', sfdEn);
    epxDmn.addTranslations('es', sfdEs);
    epxDmn.addTranslations('pt', sfdPt);
    for (const [name, theme] of Object.entries(SFD_THEMES)) {
      epxDmn.registerTheme(name, theme);
    }

    // Toggle the DMN properties panel visibility on selection (mirrors BPMN UX).
    const dmnPropEl = document.getElementById('dmn-properties');
    epxDmn.on('element.click', ({ element }) => {
      if (!dmnPropEl) return;
      if (element) dmnPropEl.classList.remove('is-hidden');
      else dmnPropEl.classList.add('is-hidden');
    });
    // EpxDmn emits per-surface change events; any of them means "dirty".
    const markDirty = () => {
      const status = document.getElementById('dmn-save-status');
      if (!status) return;
      status.textContent = '● Sin guardar';
      status.classList.remove('saved');
      status.classList.add('dirty', 'visible');
      clearTimeout(status._hideTimer);
    };
    epxDmn.on('decisionTable.changed', markDirty);
    epxDmn.on('expression.changed', markDirty);
    epxDmn.on('element.changed', markDirty);

    // Wire DMN view tabs → epxDmn.setView(view)
    const tabsEl = document.getElementById('dmn-view-tabs');
    if (tabsEl) {
      tabsEl.addEventListener('click', (e) => {
        const btn = e.target.closest('button[data-view]');
        if (!btn) return;
        const view = btn.dataset.view;
        if (!view || !epxDmn) return;
        try {
          epxDmn.setView(view);
          tabsEl.querySelectorAll('button').forEach((b) =>
            b.classList.toggle('active', b === btn)
          );
        } catch (err) {
          console.warn('[sfd-v2] setView failed:', err);
        }
      });
      epxDmn.on('view.changed', ({ view }) => {
        tabsEl.querySelectorAll('button').forEach((b) =>
          b.classList.toggle('active', b.dataset.view === view)
        );
        syncDmnBackButton();
      });
    }

    // Reflect simulator dock open/closed state on the toggle button.
    epxDmn.on('simulator:toggle', ({ open }) => {
      const toggle = document.getElementById('dmn-sim-toggle');
      if (toggle) toggle.classList.toggle('is-active', !!open);
    });
  }

  // Keep DMN's locale in sync with sfd-v2 across re-init (changeLocale calls
  // initModeler again; epxDmn is reused so we sync via setLocale).
  if (epxDmn && epxDmn.getLocale && epxDmn.getLocale() !== locale) {
    try { epxDmn.setLocale(locale); } catch (err) { console.warn('[sfd-v2] DMN setLocale failed:', err); }
  }

  // ── Open DMN panel on BusinessRuleTask double-click ───────────────────────
  epxFlow.getLogicFlow().on('node:dblclick', ({ data }) => {
    if (data.type === 'epx:BusinessRuleTask') {
      window.openDmnPanel(data.id, data.properties?.decisionRef);
    }
  });

  // Populate Theme and Options menus from registered themes/locales
  populateMenus();

  // Load starter diagram at default zoom (skip internal auto-fit)
  epxFlow.importXML(STARTER_XML, { autoFit: false }).then(() => {
    resetDefaultView(epxFlow);
  }).catch(err => {
    console.warn('Could not load starter diagram:', err);
  });
}

// ── Dropdown menus ───────────────────────────────────────────────────────────

window.toggleDropdown = function (toggleEl) {
  const menu = toggleEl.parentElement.querySelector('.dropdown-menu');
  const isOpen = menu.classList.contains('show');
  // Close all open menus first
  document.querySelectorAll('.dropdown-menu.show').forEach(m => m.classList.remove('show'));
  if (!isOpen) menu.classList.add('show');
};

document.addEventListener('click', function (e) {
  if (!e.target.closest('.dropdown')) {
    document.querySelectorAll('.dropdown-menu.show').forEach(m => m.classList.remove('show'));
  }
});

// ── DMN Panel ────────────────────────────────────────────────────────────────

let activeDmnBackTaskName = 'proceso';

function syncDmnBackButton() {
  const labelEl = document.getElementById('dmn-back-target');
  const btn = document.getElementById('dmn-back-btn');
  if (!labelEl || !btn) return;
  const view = epxDmn?.getView?.();
  const inDrill = view && view !== 'drd';
  if (inDrill) {
    labelEl.textContent = 'DMN';
    btn.title = 'Volver al diagrama DMN';
  } else {
    labelEl.textContent = activeDmnBackTaskName;
    btn.title = 'Volver al diagrama BPMN';
  }
}

window.handleDmnBack = function () {
  const view = epxDmn?.getView?.();
  const inDrill = view && view !== 'drd';
  if (inDrill) {
    try { epxDmn.setView('drd'); } catch (err) { console.warn('[sfd-v2] setView drd failed:', err); }
    return;
  }
  window.closeDmnPanel();
};

window.openDmnPanel = async function (nodeId, decisionRef) {
  activeDmnNodeId = nodeId;
  const titleEl = document.getElementById('dmn-overlay-title');
  if (titleEl) titleEl.textContent = decisionRef || 'Decision Table';

  const node = epxFlow?.getLogicFlow().getNodeModelById(nodeId);
  activeDmnBackTaskName = node?.properties?.name?.trim() || 'proceso';
  syncDmnBackButton();

  // Reveal the overlay first so the user sees something immediately; the
  // model load below races but we await it before notifying the AI assistant
  // so its context (and welcome message) sees the loaded definitions.
  showDmnOverlay();
  if (epxDmn?.invalidateLayout) {
    requestAnimationFrame(() => epxDmn.invalidateLayout());
  }
  if (epxDmn) epxDmn.setSimulatorStorageKey(simStorageKey(nodeId));

  if (epxDmn) {
    if (epxDmn.getView() !== DMN_VIEWS.DRD) {
      try { epxDmn.setView(DMN_VIEWS.DRD); } catch { /* ignore */ }
    }
    const lfNode = epxFlow?.getLogicFlow().getNodeModelById(nodeId);
    const storedXml = dmnStore.get(nodeId) || null;
    const nodeXml = lfNode?.properties?.dmnXml;
    const existingXml = storedXml || nodeXml || null;
    if (existingXml) {
      try {
        await epxDmn.importXML(existingXml);
      } catch (err) {
        console.warn('[sfd-v2] DMN import error:', err);
        try { await epxDmn.newDecision(decisionRef || 'Nueva Decisión'); } catch { /* noop */ }
      }
    } else {
      try { await epxDmn.newDecision(decisionRef || 'Nueva Decisión'); } catch { /* noop */ }
    }
  }

  // Reset dirty/saved indicator to a clean baseline when opening.
  const status = document.getElementById('dmn-save-status');
  if (status) {
    status.textContent = '✓ Guardado';
    status.classList.remove('dirty');
    status.classList.add('saved', 'visible');
    clearTimeout(status._hideTimer);
    status._hideTimer = setTimeout(() => status.classList.remove('visible'), 2000);
  }

  // Now the model is loaded — Themis context can read XML + drg_summary
  // and the welcome message can name the active decision correctly.
  aiAssistant?.onDmnPanelOpen(decisionRef);
};

async function persistActiveDmn() {
  if (!epxDmn || !activeDmnNodeId) return false;
  const lf = epxFlow?.getLogicFlow();
  const node = lf?.getNodeModelById(activeDmnNodeId);
  const { xml } = await epxDmn.exportXML();
  if (!xml) return false;
  dmnStore.set(activeDmnNodeId, xml);
  if (lf && node) {
    lf.setProperties(activeDmnNodeId, { dmnXml: xml });
    lf.emit('node:properties-change', {
      id: activeDmnNodeId,
      properties: { dmnXml: xml },
    });
  }
  return true;
}

function flashDmnSaveStatus(text = '✓ Guardado') {
  const status = document.getElementById('dmn-save-status');
  if (!status) return;
  status.textContent = text;
  status.classList.remove('dirty');
  status.classList.add('saved', 'visible');
  clearTimeout(status._hideTimer);
  status._hideTimer = setTimeout(() => status.classList.remove('visible'), 2000);
}

window.saveDmnPanel = async function () {
  try {
    const ok = await persistActiveDmn();
    if (ok) flashDmnSaveStatus('✓ Guardado');
  } catch (e) {
    console.warn('[sfd-v2] DMN save error:', e);
    flashDmnSaveStatus('⚠ Error al guardar');
  }
};

window.loadDmnXml = function () {
  const input = document.getElementById('dmn-file-input');
  if (input) input.click();
};

// ── DMN Simulation toggle ───────────────────────────────────────────────────
// Layout, splitter, lazy mount, and reveal-on-click all live inside epx-dmn
// (EpxDmn + SimulatorDock). sfd-v2 just owns the toolbar button.

window.toggleDmnSimulation = function () {
  if (epxDmn) epxDmn.toggleSimulator();
};

window.pasteDmnXml = function () {
  const modal = document.getElementById('xml-modal');
  const textarea = document.getElementById('xml-content');
  const actionBtn = document.getElementById('modal-action-btn');
  const titleEl = document.getElementById('modal-title');
  if (!modal || !textarea || !actionBtn) return;
  titleEl.textContent = 'Pegar DMN XML';
  textarea.value = '';
  textarea.readOnly = false;
  textarea.placeholder = 'Pegá aquí el XML DMN de reglas…';
  actionBtn.textContent = 'Cargar';
  actionBtn.onclick = async () => {
    const xml = textarea.value.trim();
    if (!xml) return;
    await importDmnXmlIntoPanel(xml);
    closeModal();
  };
  modal.classList.add('show');
  textarea.focus();
};

async function importDmnXmlIntoPanel(xml, fileName) {
  if (!epxDmn) return;
  if (!xml || !/<(?:\w+:)?definitions\b/i.test(xml)) {
    flashDmnSaveStatus('⚠ XML no válido');
    return;
  }
  try {
    await epxDmn.importXML(xml);
    if (activeDmnNodeId) {
      dmnStore.set(activeDmnNodeId, xml);
      const lf = epxFlow?.getLogicFlow();
      if (lf && lf.getNodeModelById(activeDmnNodeId)) {
        lf.setProperties(activeDmnNodeId, { dmnXml: xml });
        lf.emit('node:properties-change', {
          id: activeDmnNodeId,
          properties: { dmnXml: xml },
        });
      }
    }
    flashDmnSaveStatus(fileName ? `✓ ${fileName} cargado` : '✓ XML cargado');
  } catch (err) {
    console.warn('[sfd-v2] DMN load error:', err);
    flashDmnSaveStatus('⚠ Error al cargar XML');
  }
}

window.closeDmnPanel = async function () {
  const statusEl = document.getElementById('dmn-save-status');
  const wasDirty = statusEl?.classList.contains('dirty');
  try {
    const ok = await persistActiveDmn();
    if (ok && wasDirty) flashDmnSaveStatus('✓ Guardado');
  } catch (e) {
    console.warn('[sfd-v2] DMN auto-save error:', e);
    flashDmnSaveStatus('⚠ Error al guardar');
  }
  activeDmnNodeId = null;
  if (epxDmn) epxDmn.closeSimulator();
  hideDmnOverlay();

  aiAssistant?.onDmnPanelClose();
};

// ── Diagram ID helpers ───────────────────────────────────────────────────────

function _extractProcessId(xml) {
  if (!xml) return null;
  const m = xml.match(/<(?:\w+:)?process[^>]+\bid=["']([^"']+)["']/i);
  return m ? m[1] : null;
}

function _notifyDiagramChanged(xml) {
  const id = _extractProcessId(xml) || ('new_' + Date.now());
  aiAssistant?.onDiagramChange(id);
}

// ── Toolbar actions ──────────────────────────────────────────────────────────

window.createNew = function () {
  if (epxFlow) {
    epxFlow.clear();
    resetDefaultView(epxFlow);
    setStatusbarFile('New diagram');
    setStatusbarStatus('ok', 'Ready');
    _notifyDiagramChanged(null);
  }
};

window.openXml = function () {
  modalMode = 'import';
  document.getElementById('modal-title').textContent = 'Open XML';
  const textarea = document.getElementById('xml-content');
  textarea.value = '';
  textarea.readOnly = false;
  textarea.placeholder = 'Paste BPMN 2.0 XML here...';
  document.getElementById('modal-action-btn').textContent = 'Import';
  document.getElementById('modal-action-btn').onclick = doImport;
  document.getElementById('xml-modal').classList.add('show');
};

window.openFile = function () {
  document.getElementById('file-input').click();
};

window.exportXml = async function () {
  if (!epxFlow) return;
  const { xml, warnings } = await epxFlow.exportXML();
  modalMode = 'export';
  document.getElementById('modal-title').textContent = 'Export XML';
  document.getElementById('xml-content').value = xml;
  document.getElementById('xml-content').readOnly = true;
  document.getElementById('modal-action-btn').textContent = 'Copy';
  document.getElementById('modal-action-btn').onclick = () => {
    navigator.clipboard.writeText(xml).then(() => {
      setStatusbarStatus('ok', 'XML copied');
      setTimeout(() => setStatusbarStatus('ok', 'Ready'), 2000);
    });
  };
  document.getElementById('xml-modal').classList.add('show');
  if (warnings.length) {
    console.warn('Export warnings:', warnings);
  }
};

window.exportSvg = function () {
  if (!epxFlow) return;
  try {
    epxFlow.downloadSVG('diagram');
    setStatusbarStatus('ok', 'SVG downloaded');
    setTimeout(() => setStatusbarStatus('ok', 'Ready'), 2000);
  } catch (e) {
    console.error('[sfd-v2] exportSvg error:', e);
    setStatusbarStatus('err', 'SVG export failed');
  }
};

window.closeModal = function () {
  document.getElementById('xml-modal').classList.remove('show');
};

async function doImport() {
  const xml = document.getElementById('xml-content').value.trim();
  if (!xml) return;
  try {
    await epxFlow.importXML(xml);
    closeModal();
    _notifyDiagramChanged(xml);
  } catch (e) {
    console.error(e);
  }
}

window.doFit = function () {
  if (epxFlow) { epxFlow.fit(); setTimeout(updateZoomStatus, 150); }
};

window.doZoomIn = function () {
  if (epxFlow) { epxFlow.zoom(1.2); setTimeout(updateZoomStatus, 50); }
};

window.doZoomOut = function () {
  if (epxFlow) { epxFlow.zoom(0.8); setTimeout(updateZoomStatus, 50); }
};

window.doValidate = function () {
  if (epxFlow) epxFlow.validate();
};

window.doAutoLayout = function () {
  if (epxFlow) epxFlow.autoLayout();
};

window.changeLocale = async function (locale) {
  if (epxFlow) {
    try {
      const { xml } = await epxFlow.exportXML();
      initModeler(locale);
      if (xml) {
        epxFlow.importXML(xml).catch(err => {
          console.warn('[sfd-v2] Could not restore diagram after locale change:', err);
        });
      }
    } catch {
      initModeler(locale);
    }
  } else {
    initModeler(locale);
  }
};

window.changeTheme = function (themeName) {
  if (!epxFlow) return;
  epxFlow.setTheme(themeName);
  if (epxDmn) {
    try { epxDmn.setTheme(themeName); } catch (err) { console.warn('[sfd-v2] DMN setTheme failed:', err); }
  }
  setStatusbarStatus('ok', `Theme: ${themeName}`);
  setTimeout(() => setStatusbarStatus('ok', 'Ready'), 2000);
};

window.changeGrid = function (key) {
  if (!epxFlow) return;
  const opt = GRID_OPTIONS.find(o => o.key === key);
  if (!opt) return;
  _currentGridKey = key;
  if (key === 'off') {
    epxFlow.setGridVisible(false);
    if (epxDmn) { try { epxDmn.setGridVisible(false); } catch (err) { console.warn('[sfd-v2] DMN setGridVisible failed:', err); } }
  } else {
    epxFlow.setGrid({ visible: true, type: opt.type, size: opt.size });
    if (epxDmn) { try { epxDmn.setGrid({ visible: true, type: opt.type, size: opt.size }); } catch (err) { console.warn('[sfd-v2] DMN setGrid failed:', err); } }
  }
  // Refresh active state in the menu
  document.querySelectorAll('#grid-menu .dropdown-item').forEach(btn => {
    btn.classList.toggle('dropdown-item--active', btn.dataset.gridKey === key);
  });
  setStatusbarStatus('ok', `Grid: ${opt.label}`);
  setTimeout(() => setStatusbarStatus('ok', 'Ready'), 2000);
};

// ── File input handler ───────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  document.getElementById('file-input').addEventListener('change', async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setStatusbarStatus('warn', 'Loading…');
    const text = await file.text();
    try {
      await epxFlow.importXML(text);
      setStatusbarFile(file.name);
      setStatusbarStatus('ok', 'Loaded');
      setTimeout(() => setStatusbarStatus('ok', 'Ready'), 2000);
      _notifyDiagramChanged(text);
    } catch (err) {
      console.error('[sfd-v2] file import error:', err);
      setStatusbarStatus('err', `Error: ${err.message}`);
    }
    e.target.value = '';
  });

  const dmnFileInput = document.getElementById('dmn-file-input');
  if (dmnFileInput) {
    dmnFileInput.addEventListener('change', async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        const text = await file.text();
        await importDmnXmlIntoPanel(text, file.name);
      } catch (err) {
        console.warn('[sfd-v2] DMN file read error:', err);
        flashDmnSaveStatus('⚠ No se pudo leer el archivo');
      }
      e.target.value = '';
    });
  }
});

// ── Sample loader ────────────────────────────────────────────────────────────

const SAMPLE_LABELS = {
  'sencillo-poc.xml': 'SENCILLO — Propuesta POC IN',
  'sencillo-transporte.xml': 'SENCILLO — Transporte de Mercancia',
  'medio-liquidacion.xml': 'MEDIO — Liquidacion de Contratos',
  'medio-proceso-liquidacion.xml': 'MEDIO — Proceso de Liquidacion',
  'dificil-actos-admin.xml': 'DIFICIL — Actos Administrativos',
  'dificil-ani.xml': 'DIFICIL — Modelo Integracion ANI',
  'brms-evaluacion-credito.xml': 'BRMS — Evaluación de Crédito',
  'brms-cotizacion-seguro-auto.xml': 'BRMS — Cotización Seguro de Auto',
};

window.loadSample = async function (file) {
  if (!epxFlow) return;
  const bar = document.getElementById('progress-bar');
  if (bar) bar.style.width = '30%';
  setStatusbarStatus('warn', 'Loading…');
  try {
    const resp = await fetch(`samples/${file}`);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const xml = await resp.text();
    if (bar) bar.style.width = '70%';
    const result = await epxFlow.importXML(xml);
    if (result?.warnings?.length) {
      console.warn('[sfd-v2] import warnings:', result.warnings);
    }
    await new Promise(r => setTimeout(r, 200));
    epxFlow.fit();
    setTimeout(updateZoomStatus, 200);
    if (bar) { bar.style.width = '100%'; setTimeout(() => { bar.style.width = '0%'; }, 800); }
    const sampleLabel = SAMPLE_LABELS[file] || file;
    setStatusbarFile(sampleLabel);
    setStatusbarStatus('ok', 'Ready');
    _notifyDiagramChanged(xml);
  } catch (err) {
    if (bar) bar.style.width = '0%';
    setStatusbarStatus('err', `Error: ${err.message}`);
    console.error('[sfd-v2] loadSample error:', err);
  }
};

/**
 * Loads a BPMN sample that has companion DMN file(s).
 *
 * Supported call shapes:
 *   loadBrmsSample(bpmn, 'one.dmn', 'decisionId')   → seed BRTs whose decisionRef matches
 *   loadBrmsSample(bpmn, 'one.dmn', null)           → seed every BRT with the same DMN
 *   loadBrmsSample(bpmn, { ref1: 'a.dmn', ref2: 'b.dmn' })  → per-BRT DMN files
 *
 * Each BRT receives its own dmnXml in node properties so the DMN editor
 * opens pre-populated on double-click — independently per task.
 */
window.loadBrmsSample = async function (bpmnFile, dmnSpec, decisionRef) {
  if (!epxFlow) return;
  const bar = document.getElementById('progress-bar');
  if (bar) bar.style.width = '20%';
  setStatusbarStatus('warn', 'Loading…');

  try {
    const bpmnResp = await fetch(`samples/${bpmnFile}`);
    if (!bpmnResp.ok) throw new Error(`BPMN HTTP ${bpmnResp.status}`);
    const bpmnXml = await bpmnResp.text();

    // Resolve dmnSpec → array of { matcher, xml } where matcher is either a
    // decisionRef string or '*' (any BRT).
    const dmnEntries = [];
    if (typeof dmnSpec === 'string') {
      const r = await fetch(`samples/${dmnSpec}`);
      if (!r.ok) throw new Error(`DMN HTTP ${r.status}`);
      const xml = await r.text();
      dmnEntries.push({ matcher: decisionRef || '*', xml });
    } else if (dmnSpec && typeof dmnSpec === 'object') {
      const pairs = await Promise.all(
        Object.entries(dmnSpec).map(async ([ref, file]) => {
          const r = await fetch(`samples/${file}`);
          if (!r.ok) throw new Error(`DMN ${file} HTTP ${r.status}`);
          return { matcher: ref, xml: await r.text() };
        })
      );
      dmnEntries.push(...pairs);
    }
    if (bar) bar.style.width = '50%';

    const result = await epxFlow.importXML(bpmnXml);

    // Seed each BusinessRuleTask with its matching DMN xml.
    if (dmnEntries.length) {
      const wildcard = dmnEntries.find((e) => e.matcher === '*');
      const byRef = new Map(dmnEntries.filter((e) => e.matcher !== '*').map((e) => [e.matcher, e.xml]));
      const lf = epxFlow.getLogicFlow();
      const nodes = lf?.graphModel?.nodes || [];
      nodes.forEach((n) => {
        if (n.type !== 'epx:BusinessRuleTask') return;
        const ref = n.properties?.decisionRef;
        const xml = byRef.get(ref) || wildcard?.xml;
        if (!xml) return;
        dmnStore.set(n.id, xml);
        lf.setProperties(n.id, { dmnXml: xml });
      });
      // Pre-seed the editor with the first DMN so the panel isn't empty.
      const firstXml = dmnEntries[0]?.xml;
      if (firstXml && epxDmn) {
        try { epxDmn.importXML(firstXml); } catch (e) { /* ignore pre-seed errors */ }
      }
    }
    if (result?.warnings?.length) {
      console.warn('[sfd-v2] import warnings:', result.warnings);
    }
    await new Promise(r => setTimeout(r, 200));
    epxFlow.fit();
    setTimeout(updateZoomStatus, 200);

    if (bar) { bar.style.width = '100%'; setTimeout(() => { bar.style.width = '0%'; }, 800); }
    const sampleLabel = SAMPLE_LABELS[bpmnFile] || bpmnFile;
    setStatusbarFile(sampleLabel);
    setStatusbarStatus('ok', 'Ready');
    _notifyDiagramChanged(bpmnXml);
  } catch (err) {
    if (bar) bar.style.width = '0%';
    setStatusbarStatus('err', `Error: ${err.message}`);
    console.error('[sfd-v2] loadBrmsSample error:', err);
  }
};

// ── Bootstrap ────────────────────────────────────────────────────────────────

initModeler('en');

// Mount popup/dock/full mode toggle, Esc, click-outside and resize handle.
// Close goes through closeDmnPanel so auto-save still runs.
mountDmnOverlayModes({
  onClose: () => window.closeDmnPanel(),
  onResize: () => epxDmn?.invalidateLayout?.(),
  onModeChange: () => {
    if (epxDmn?.invalidateLayout) {
      requestAnimationFrame(() => epxDmn.invalidateLayout());
    }
  },
});

// Notify the canvas library whenever the canvas element is resized by CSS
// (e.g. when the properties or AI chat panel opens/closes and shifts the canvas).
const _canvasEl = document.getElementById('canvas');
if (_canvasEl) {
  new ResizeObserver(() => {
    try {
      epxFlow?._lf?.resize?.();
    } catch (_) {
      // fallback: window resize event triggers most canvas libraries
      window.dispatchEvent(new Event('resize'));
    }
  }).observe(_canvasEl);
}

// Mount AI Assistant after epxFlow is initialized (window.epxFlow set in initModeler)
if (process.env.AI_ASSISTANT_ENABLED) {
  const aiAssistant = new AiAssistant();
  aiAssistant.mount();
  window.aiAssistant = aiAssistant;
}
